import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';

const C = {
  bg:'#1a1c28', card:'#252838', border:'#2e3150', text:'#e2e8f0',
  muted:'#64748b', accent:'#f97316', green:'#4ade80', red:'#f87171',
  blue:'#60a5fa', amber:'#fbbf24', purple:'#a78bfa',
};

const ROLE_COLOR = { owner:'#f97316', admin:'#60a5fa', staff:'#4ade80', viewer:'#a78bfa' };
const ROLE_DESC  = { owner:'Full access — can manage billing and team', admin:'Can manage orders, clients, and inventory', staff:'Can view and create orders and clients', viewer:'Read-only access' };

export default function TeamManagement({ currentUserId }) {
  const [members,   setMembers]   = useState([]);
  const [invites,   setInvites]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [email,     setEmail]     = useState('');
  const [role,      setRole]      = useState('staff');
  const [inviting,  setInviting]  = useState(false);
  const [flash,     setFlash]     = useState(null);
  const [copied,    setCopied]    = useState(null);
  const [addMode,   setAddMode]   = useState('invite'); // 'invite' | 'direct'
  const [dirEmail,  setDirEmail]  = useState('');
  const [dirName,   setDirName]   = useState('');
  const [dirPass,   setDirPass]   = useState('');
  const [dirRole,   setDirRole]   = useState('staff');
  const [dirSaving, setDirSaving] = useState(false);

  const showFlash = (msg, type='success') => {
    setFlash({ msg, type });
    setTimeout(() => setFlash(null), 4000);
  };

  const load = async () => {
    try {
      const r = await axios.get('/api/rental-team/members');
      setMembers(r.data.members || []);
      setInvites(r.data.invites || []);
    } catch(e) { showFlash('Failed to load team', 'error'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const sendInvite = async () => {
    if (!email.trim()) return;
    setInviting(true);
    try {
      const r = await axios.post('/api/rental-team/invite', { email: email.trim(), role });
      showFlash(`Invite sent to ${email}`);
      setEmail('');
      load();
      // Also show copy link in case email fails
      if (r.data.acceptUrl) setCopied({ url: r.data.acceptUrl, id: 'new' });
    } catch(e) { showFlash(e.response?.data?.error || 'Failed to send invite', 'error'); }
    setInviting(false);
  };

  const addDirect = async () => {
    if (!dirEmail.trim() || !dirPass.trim()) return;
    setDirSaving(true);
    try {
      await axios.post('/api/rental-team/add-direct', { email: dirEmail.trim(), password: dirPass, role: dirRole, name: dirName.trim() });
      showFlash(`${dirEmail} added to your team`);
      setDirEmail(''); setDirName(''); setDirPass('');
      load();
    } catch(e) { showFlash(e.response?.data?.error || 'Failed to add member', 'error'); }
    setDirSaving(false);
  };

  const changeRole = async (userId, newRole) => {
    try {
      await axios.patch(`/api/rental-team/members/${userId}/role`, { role: newRole });
      showFlash('Role updated');
      load();
    } catch(e) { showFlash(e.response?.data?.error || 'Failed', 'error'); }
  };

  const removeMember = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from your team?`)) return;
    try {
      await axios.delete(`/api/rental-team/members/${userId}`);
      showFlash(`${name} removed`);
      load();
    } catch(e) { showFlash(e.response?.data?.error || 'Failed', 'error'); }
  };

  const cancelInvite = async (id) => {
    try {
      await axios.delete(`/api/rental-team/invites/${id}`);
      showFlash('Invite cancelled');
      load();
    } catch(e) { showFlash('Failed', 'error'); }
  };

  const resendInvite = async (id, email) => {
    try {
      await axios.post(`/api/rental-team/resend/${id}`);
      showFlash(`Resent to ${email}`);
    } catch(e) { showFlash('Failed to resend', 'error'); }
  };

  const copyLink = (url, id) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied({ url, id });
      setTimeout(() => setCopied(null), 3000);
    });
  };

  const inp = { padding:'9px 12px', borderRadius:7, border:`1px solid ${C.border}`, background:'#1e2235', color:C.text, fontSize:13, outline:'none' };
  const card = { background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'20px 24px', marginBottom:14 };

  return (
    <div style={{ maxWidth:720 }}>
      {/* Flash */}
      {flash && (
        <div style={{ position:'fixed', top:20, right:20, zIndex:9999, padding:'12px 20px', borderRadius:10, background:flash.type==='error'?'#3b0a0a':'#14532d', color:flash.type==='error'?C.red:C.green, fontWeight:700, fontSize:13, boxShadow:'0 4px 16px rgba(0,0,0,0.4)' }}>
          {flash.msg}
        </div>
      )}

      <div style={{ fontSize:20, fontWeight:800, color:C.text, marginBottom:4 }}>👥 Team Members</div>
      <div style={{ fontSize:13, color:C.muted, marginBottom:20 }}>Invite your staff to access the CRM with their own login.</div>

      {/* Add member form */}
      <div style={card}>
        {/* Mode toggle */}
        <div style={{ display:'flex', gap:4, marginBottom:18, background:'#181928', borderRadius:8, padding:4 }}>
          {[['invite','✉ Send Invite'],['direct','🔑 Set Password']].map(([m,l]) => (
            <button key={m} onClick={() => setAddMode(m)}
              style={{ flex:1, padding:'7px', borderRadius:6, border:'none', background: addMode===m ? C.accent : 'transparent', color: addMode===m ? '#fff' : C.muted, fontWeight:700, fontSize:12, cursor:'pointer' }}>
              {l}
            </button>
          ))}
        </div>

        {addMode === 'invite' && (
          <>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <input style={{ ...inp, flex:1, minWidth:200 }} type="email" placeholder="colleague@email.com" value={email}
                onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendInvite()} />
              <select style={{ ...inp, width:140 }} value={role} onChange={e => setRole(e.target.value)}>
                <option value="admin">Admin</option>
                <option value="staff">Staff</option>
                <option value="viewer">Viewer</option>
              </select>
              <button onClick={sendInvite} disabled={inviting || !email.trim()}
                style={{ padding:'9px 20px', borderRadius:8, border:'none', background:C.accent, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', opacity:inviting||!email.trim()?0.5:1, whiteSpace:'nowrap' }}>
                {inviting ? 'Sending...' : '✉ Send Invite'}
              </button>
            </div>
            <div style={{ marginTop:10, fontSize:11, color:C.muted }}>{ROLE_DESC[role]}</div>
            <div style={{ marginTop:14, display:'flex', gap:8, flexWrap:'wrap' }}>
              {Object.entries(ROLE_DESC).map(([r]) => (
                <div key={r} style={{ fontSize:10, padding:'3px 8px', borderRadius:5, background:`${ROLE_COLOR[r]}18`, color:ROLE_COLOR[r], fontWeight:700, cursor:'pointer', border:`1px solid ${ROLE_COLOR[r]}33` }}
                  onClick={() => setRole(r)}>{r}</div>
              ))}
            </div>
          </>
        )}

        {addMode === 'direct' && (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Name</div>
                <input style={inp} placeholder="Full name" value={dirName} onChange={e => setDirName(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Email</div>
                <input style={inp} type="email" placeholder="email@example.com" value={dirEmail} onChange={e => setDirEmail(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Password</div>
                <input style={inp} type="password" placeholder="Min 8 characters" value={dirPass} onChange={e => setDirPass(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Role</div>
                <select style={inp} value={dirRole} onChange={e => setDirRole(e.target.value)}>
                  <option value="admin">Admin</option>
                  <option value="staff">Staff</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>
            <button onClick={addDirect} disabled={dirSaving || !dirEmail.trim() || !dirPass.trim()}
              style={{ padding:'9px 20px', borderRadius:8, border:'none', background:C.accent, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', opacity:dirSaving||!dirEmail.trim()||!dirPass.trim()?0.5:1 }}>
              {dirSaving ? 'Adding...' : '+ Add Member'}
            </button>
            <div style={{ marginTop:8, fontSize:11, color:C.muted }}>Creates an account immediately — share credentials with the team member directly.</div>
          </>
        )}
      </div>

      {/* Copy link for last invite */}
      {copied && copied.id === 'new' && (
        <div style={{ ...card, background:'rgba(74,222,128,0.06)', border:`1px solid ${C.green}33`, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:C.green, marginBottom:3 }}>✓ Invite sent — copy the link as backup</div>
            <div style={{ fontSize:11, color:C.muted, wordBreak:'break-all' }}>{copied.url}</div>
          </div>
          <button onClick={() => navigator.clipboard.writeText(copied.url)}
            style={{ padding:'6px 12px', borderRadius:6, border:`1px solid ${C.green}44`, background:'transparent', color:C.green, fontSize:11, cursor:'pointer', whiteSpace:'nowrap' }}>
            Copy
          </button>
        </div>
      )}

      {/* Current members */}
      <div style={card}>
        <div style={{ fontSize:12, fontWeight:700, color:C.accent, textTransform:'uppercase', letterSpacing:1, marginBottom:14 }}>
          Active Members ({members.length})
        </div>
        {loading ? (
          <div style={{ color:C.muted, fontSize:13, padding:16, textAlign:'center' }}>Loading...</div>
        ) : members.length === 0 ? (
          <div style={{ color:C.muted, fontSize:13, padding:16, textAlign:'center' }}>No members yet</div>
        ) : (
          members.filter(m => m.email).map(m => (
            <div key={m.user_id} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 0', borderBottom:`1px solid ${C.border}` }}>
              {/* Avatar */}
              <div style={{ width:38, height:38, borderRadius:'50%', background:`${ROLE_COLOR[m.role]}22`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:ROLE_COLOR[m.role], flexShrink:0 }}>
                {(m.name||m.email||'?').charAt(0).toUpperCase()}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:13, color:C.text }}>{m.name || m.email}</div>
                <div style={{ fontSize:11, color:C.muted }}>{m.email}{m.last_login ? ` · Last login ${new Date(m.last_login).toLocaleDateString()}` : ''}</div>
              </div>
              {/* Role selector */}
              {m.role === 'owner' ? (
                <span style={{ fontSize:11, fontWeight:700, color:ROLE_COLOR.owner, padding:'3px 10px', borderRadius:20, background:`${ROLE_COLOR.owner}18`, border:`1px solid ${ROLE_COLOR.owner}33` }}>owner</span>
              ) : (
                <select value={m.role} onChange={e => changeRole(m.user_id, e.target.value)}
                  style={{ ...inp, padding:'5px 8px', fontSize:11, width:100 }}>
                  <option value="admin">admin</option>
                  <option value="staff">staff</option>
                  <option value="viewer">viewer</option>
                </select>
              )}
              {m.role !== 'owner' && m.user_id !== currentUserId && (
                <button onClick={() => removeMember(m.user_id, m.name||m.email)}
                  style={{ padding:'5px 10px', borderRadius:6, border:`1px solid #3b0a0a`, background:'transparent', color:C.red, fontSize:11, cursor:'pointer' }}>
                  Remove
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pending invites */}
      {invites.length > 0 && (
        <div style={card}>
          <div style={{ fontSize:12, fontWeight:700, color:C.amber, textTransform:'uppercase', letterSpacing:1, marginBottom:14 }}>
            Pending Invites ({invites.length})
          </div>
          {invites.map(inv => (
            <div key={inv.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:`1px solid ${C.border}` }}>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:600, fontSize:13, color:C.text }}>{inv.email}</div>
                <div style={{ fontSize:11, color:C.muted }}>
                  <span style={{ color:ROLE_COLOR[inv.role], fontWeight:700 }}>{inv.role}</span>
                  {' · Expires '}{new Date(inv.expires_at).toLocaleDateString()}
                </div>
              </div>
              <button onClick={() => resendInvite(inv.id, inv.email)}
                style={{ padding:'4px 10px', borderRadius:6, border:`1px solid ${C.border}`, background:'transparent', color:C.blue, fontSize:11, cursor:'pointer' }}>
                Resend
              </button>
              <button onClick={() => cancelInvite(inv.id)}
                style={{ padding:'4px 10px', borderRadius:6, border:`1px solid #3b0a0a`, background:'transparent', color:C.red, fontSize:11, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
